import React, { useState } from "react";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText, { IDtvTextProps } from "@directv-digital/dds.ui.dtv-text";
import DtvModal from "@directv-digital/dds.ui.dtv-modal";
import CardMedia from "@mui/material/CardMedia";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import { IDtvButtonProps } from "@directv-digital/dds.ui.dtv-button";

import { ChannelGrid } from "./ChannelGrid";

export interface MyFreeDtvBannerProps {
  logo?: string;
  title?: IDtvTextProps;
  linkText?: IDtvTextProps;
  modalData?: {
    title?: IDtvTextProps;
    cta?: IDtvButtonProps;
  };
  channels?: IMyFreeDtvChannel[];
  theming?: {
    background?: string; // color token or hex value
    textColor?: string; // color token or hex value
    border?: string; // color token or hex value
  };
};

export interface IMyFreeDtvChannel {
  channelNameLabel: string;
  channelLogoPath: string;
};

const styles = {
  containerStyles: {
    display: "flex",
    padding: {
      xs: "12px",
      sm: "12px 20px"
    },
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    gap: {
      xs: "12px",
      sm: "32px"
    },
    alignSelf: "stretch",
    borderRadius: "4px"
  },
  logoContainer: {
    width: {
      xs: "56px",
      sm: "72px",
      md: "97px"
    },
    height: {
      xs: "16px",
      sm: "20px",
      md: "27px"
    }
  },
  titleContainer: {
    display: "flex",
    flexFlow: {
      xs: "column nowrap",
      sm: "row wrap"
    }
  },
  modalStyle: {
    /**
     * If below is changed from 90vh, the logo grid calculations in ChannelGrid.tsx
     * will need to be updated as well.
     */
    maxHeight: {
      xs: "calc(90vh)",
      sm: "calc(90vh)",
      md: "calc(90vh)",
      lg: "calc(90vh)",
      xl: "calc(90vh)"
    },
    height: {
      xs: "calc(90vh)",
      sm: "calc(90vh)",
      md: "calc(90vh)",
      lg: "calc(90vh)",
      xl: "calc(90vh)"
    },
    maxWidth: {
      xl: "1028px",
      lg: "900px",
      md: "900px",
      sm: "600px",
      xs: "100%"
    },
    background: "#FFF"
  },
  modalBodyStyle: {
    m: {
      xs: "0px 0px 20px 18px",
      sm: "0px 32px 32px 32px",
      md: "0px 48px 32px 48px"
    },
    borderRadius: "0px 0px 12px 12px",
    position: "relative",
    maxHeight: {
      xs: "calc(100% - 36px)",
      sm: "calc(100% - 50px)",
      md: "calc(100% - 58px)",
      lg: "calc(100% - 58px)",
      xl: "calc(100% - 58px)"
    },
    height: {
      xs: "calc(100% - 36px)",
      sm: "calc(100% - 50px)",
      md: "calc(100% - 58px)",
      lg: "calc(100% - 58px)",
      xl: "calc(100% - 58px)"
    }
  }
};

export function MyFreeDtvBanner(props: MyFreeDtvBannerProps) {
  try {
    const [open, setOpen] = useState(false);
    const {
      channels: myFreeChannels,
      logo, title, linkText, modalData, theming
    } = props;

    const handleOpenModal = () => setOpen(true);
    const handleCloseModal = () => setOpen(false);

    const handleLinkClick = () => {
      handleOpenModal();
    };
    return (
      <DtvBox sx={{
        ...styles.containerStyles,
        background: theming?.background,
        outline: `1px solid ${theming?.border || "#E0E0E0"}`
      }}>
        {/* logo container */}
        <DtvBox
          aria-label={"My Free Directv"}
          id="icon-container"
          sx={{ ...styles.logoContainer }}
          tabIndex={-1}
        >
          {/* logo */}
          <CardMedia
            component={"img"}
            src={logo}
            // loading="lazy"
            alt={"myfree directv"}
          />
        </DtvBox>
        {/* title & link */}
        <DtvBox sx={{ ...styles.titleContainer }}>
          <DtvText
            content={`${title  }&nbsp`}
            sx={{ color: theming?.textColor }}
            variant={"smalltextmedium"}
          />
          <DtvText
            content={linkText?.toString()}
            tabIndex={0}
            variant={"smalltextmedium"}
            sx={{
              textDecoration: "underline",
              color: theming?.textColor,
              cursor: "pointer"
            }}
            {...getAnalyticsAttrs((linkText as string)?.toLowerCase() || "get a list of channels here.", "panel_channel_listing_page", "/modal/channel-lineup")}
            onClick={handleLinkClick}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleLinkClick();
              }
            }}
          />
        </DtvBox>
        {/* modal content */}
        <DtvModal
          handleClose={() => handleCloseModal()}
          modalBodyStyle={styles.modalBodyStyle}
          modalStyle={styles.modalStyle}
          open={open}
        >
          <ChannelGrid
            channels={myFreeChannels}
            cta={modalData?.cta}
            title={modalData?.title}
          />
        </DtvModal>
      </DtvBox>
    );
  } catch (error) {
    console.error(`MyFreeDirectvBanner error :: ${error}`);
    return <ErrorDisplay component="MyFreeDirectvBanner" error={error} />;
  }
}
